import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useIsOverlayOpen } from "../screens/reader/hooks/hookstate/useIsOverlayOpen";
import { useSlideInMenu } from "../components/slideInMenu/hooks/useSlideInMenu";
import { useIsSidebarOpen } from "./useIsSidebarOpen";
import { useIsSmallBreakpoint, useMobileBreakpoint } from "../utils/useBreakpoints";
import { readerComponentsStore } from "../hookstate/readerComponentStore";
import { isReaderRoute } from "../utils/isReaderRoute";

export function useDisableBodyScroll() {
  const { isOverlayOpen } = useIsOverlayOpen();
  const { openMenu } = useSlideInMenu();
  const { isSidebarOpen } = useIsSidebarOpen();
  const isSmallBreakpoint = useIsSmallBreakpoint();
  const isMobile = useMobileBreakpoint();
  const location = useLocation();

  // Close reader overlay on any pathname change (navigating away or switching modes)
  useEffect(() => {
    if (!isOverlayOpen) return;

    readerComponentsStore.isOverlayOpen.set(false);

    // Restore scroll if leaving reader
    if (!isReaderRoute(location.pathname)) {
      document.documentElement.style.overflowX = "hidden";
      document.documentElement.style.overflowY = "auto";
      document.documentElement.style.scrollbarGutter = "";
    }
  }, [location.pathname]);

  useEffect(() => {
    // The reader overlay is a transparent in-reader UI; locking body scroll
    // would hide the scrollbar and reflow the page. Slide-in menus inside the
    // reader still lock — they cover the page and shouldn't scroll behind.
    const isReader = isReaderRoute(location.pathname);
    const shouldDisableScroll =
      (isOverlayOpen && !isReader) ||
      openMenu ||
      (isSmallBreakpoint && isSidebarOpen);

    if (shouldDisableScroll) {
      // Check if scrollbar is currently showing
      const hasScrollbar =
        document.documentElement.scrollHeight >
        document.documentElement.clientHeight;

      document.documentElement.style.overflowX = "hidden";
      document.documentElement.style.overflowY = "hidden";

      // Reserve scrollbar space on anything wider than true mobile (md+) so
      // the page doesn't gain ~15px when scroll is locked — that width gain
      // causes virtualised grids to reflow (e.g. browse 3 → 4 columns).
      // Mobile uses overlay scrollbars so no gutter is needed.
      if (hasScrollbar && !isMobile) {
        document.documentElement.style.scrollbarGutter = "stable";
      }
    } else {
      document.documentElement.style.overflowX = "hidden";
      document.documentElement.style.overflowY = "auto";
      document.documentElement.style.scrollbarGutter = "";
    }

    return () => {
      document.documentElement.style.overflowX = "hidden";
      document.documentElement.style.overflowY = "auto";
      document.documentElement.style.scrollbarGutter = "";
    };
  }, [isOverlayOpen, openMenu, isSidebarOpen, isSmallBreakpoint, isMobile]);
}
